import { REDUCED_MOTION } from "./ruleGraphReducedMotion";
import { runLand } from "./ruleGraphRunLand";
import { useNodeMeasurement } from "./useNodeMeasurement";
import { type CSSProperties, useLayoutEffect, useRef } from "react";
import { useImmer } from "use-immer";

import type { RuleGraphNode, RuleGraphNodeKind } from "@intelligems/ig-types";
import { IgChip, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { Box, Skeleton, Stack, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Handle, type NodeProps, Position } from "@xyflow/react";
import {
  ArrowUpDown,
  CircleSlash,
  Database,
  Filter,
  GitBranch,
  type LucideIcon,
  PackageCheck,
  Play,
  Scissors,
  ShieldCheck,
} from "lucide-react";

import {
  RULE_GRAPH_CARD_INDENT,
  RULE_GRAPH_HEADER_GAP,
  RULE_GRAPH_NODE_HEIGHT,
  RULE_GRAPH_NODE_WIDTH,
} from "./layout";
import { RuleGraphLaneLabel } from "./RuleGraphLaneLabel";
import { RuleGraphShopperOutput } from "./RuleGraphShopperOutput";
import { RUN_WALK_LAND_MS } from "./run-walk";
import type { RuleGraphCardFlowNode } from "./ruleGraphNodeData/RuleGraphCardFlowNode";
import { fallbackTerminalBodyText } from "./ruleGraphNodeCopy/fallbackTerminalBodyText";
import { KIND_EYEBROWS } from "./ruleGraphNodeCopy/KIND_EYEBROWS";
import { nodeBodyText } from "./ruleGraphNodeCopy/nodeBodyText";
import { ruleGraphFlowNodeSize } from "./ruleGraphFlowNodeSize";

/**
 * Figma "Canvas / Step node" skeleton bar: a 7px gray/07 pill. Lives inside
 * the card's normal (fixed-size) content box, so the real text landing causes
 * zero layout shift.
 */
const SKELETON_BAR_HEIGHT = 7;

const SkeletonBar = ({ width }: { width: number }) => (
  <Skeleton
    aria-hidden
    variant="rounded"
    width={width}
    height={SKELETON_BAR_HEIGHT}
    sx={(theme) => ({
      backgroundColor: theme.igPrimitives.gray[600],
      // Figma rounds the bar ends fully, not to the card's corner radius.
      // A px string, because a bare number is scaled by the theme's shape unit.
      borderRadius: "999px",
      flexShrink: 0,
      [REDUCED_MOTION]: { animation: "none" },
    })}
  />
);

interface KindAccent {
  icon: LucideIcon;
  kindLabel: string;
  foreground: (theme: Theme) => string;
  /** Anchors (input/output/terminal) render the compact card: no summary row. */
  compact?: boolean;
}

/**
 * One card for all graph kinds — they differ only in icon, accent and which meta
 * chips render, unlike the WorkflowEditor's per-node config forms.
 */
const KIND_ACCENTS: Record<RuleGraphNodeKind, KindAccent> = {
  input: {
    icon: Play,
    kindLabel: "Input",
    foreground: (theme) => theme.igPrimitives.slate[900],
    compact: true,
  },
  branch: {
    icon: GitBranch,
    kindLabel: "Branch",
    foreground: (theme) => theme.igPrimitives.blue.A300,
  },
  generator: {
    icon: Database,
    kindLabel: "Source",
    foreground: (theme) => theme.igPrimitives.purple[900],
  },
  filter: {
    icon: Filter,
    kindLabel: "Filter",
    foreground: (theme) => theme.igPrimitives.teal[900],
  },
  sort: {
    icon: ArrowUpDown,
    kindLabel: "Sort",
    foreground: (theme) => theme.igPrimitives.indigo[900],
  },
  limit: {
    icon: Scissors,
    kindLabel: "Limit",
    foreground: (theme) => theme.igPrimitives.orange[900],
  },
  exclusions: {
    icon: ShieldCheck,
    kindLabel: "Exclusions",
    foreground: (theme) => theme.igPrimitives.slate[900],
  },
  output: {
    icon: PackageCheck,
    kindLabel: "Output",
    foreground: (theme) => theme.igPrimitives.green[900],
    compact: true,
  },
  "fallback-terminal": {
    icon: CircleSlash,
    kindLabel: "Fallback",
    foreground: (theme) => theme.igPrimitives.amber[900],
    compact: true,
  },
};

const metaChips = (node: RuleGraphNode): string[] => {
  const chips: string[] = [];
  const itemCount = node.meta?.itemCount;
  if (typeof itemCount === "number") {
    chips.push(`${itemCount} item${itemCount === 1 ? "" : "s"}`);
  }
  const intent = node.meta?.intent;
  if (typeof intent === "string") {
    chips.push(intent);
  }
  return chips;
};

type RuleGraphCardState = "skeleton" | "faded" | "default";

const cardState = ({
  skeleton,
  isFaded,
}: {
  skeleton: boolean;
  isFaded: boolean;
}): RuleGraphCardState => {
  if (skeleton) {
    return "skeleton";
  }
  if (isFaded) {
    return "faded";
  }
  return "default";
};

/**
 * The lane dot's tone. Without a scenario overlay (or while the graph
 * rebuilds) no evaluation is meaningful, so every dot is neutral.
 */
const laneDotStateOf = ({
  skeleton,
  hasScenarioOverlay,
  isFaded,
}: {
  skeleton: boolean;
  hasScenarioOverlay: boolean;
  isFaded: boolean;
}): "default" | "pending" | "filled" => {
  if (skeleton || !hasScenarioOverlay) {
    return "default";
  }
  return isFaded ? "pending" : "filled";
};

/**
 * Which header sentence the row speaks. A row past the first rung of a
 * fallback ladder is reached only because the row above came up empty, so it
 * opens with "If nothing matches" — and composes its own condition onto that
 * when it still has one.
 */
const laneLabelTypeOf = ({
  kind,
  priority,
  headerCondition,
}: {
  kind: RuleGraphNodeKind;
  priority: number | undefined;
  headerCondition: string | undefined;
}): "when" | "fallback" | "fallback-when" => {
  if (kind === "fallback-terminal") {
    return "fallback";
  }
  if (priority === undefined || priority <= 1) {
    return "when";
  }
  return headerCondition === undefined ? "fallback" : "fallback-when";
};

const cardBorderColor = (
  theme: Theme,
  { isSelected, isFaded }: { isSelected: boolean; isFaded: boolean }
): string => {
  if (isSelected) {
    return theme.igPrimitives.gray.A100;
  }
  if (isFaded) {
    return theme.igBorders.core.disabled;
  }
  return theme.igBorders.core.default;
};

/** Chip metrics shared by every chip a card can render. */
const cardChipSx = (isFaded: boolean) => ({
  flexShrink: 0,
  height: 24,
  maxWidth: 220,
  opacity: isFaded ? 0.4 : 1,
});

/**
 * Card text clipped to the room it has, with the whole of it in a tooltip when
 * — and only when — it is actually clipped. Cards are a fixed size so a long
 * label or summary loses its tail; measuring on hover keeps the tooltip off
 * every card that already reads in full.
 */
const ClippedText = ({
  text,
  lines,
  variant,
  fontWeight,
  color,
  wrapperStyle,
  fontSize,
  lineHeight,
}: {
  text: string;
  /** 1 clips with an ellipsis; 2 clamps to two lines. */
  lines: 1 | 2;
  variant: "body2" | "caption";
  fontWeight?: number;
  color?: string;
  /** Layout for the tooltip's wrapper span, which becomes the flex child. */
  wrapperStyle?: CSSProperties;
  /** Override the variant's metrics to sit on a neighbor's baseline. */
  fontSize?: number;
  lineHeight?: number;
}) => {
  const textRef = useRef<HTMLElement | null>(null);
  const [isClipped, setIsClipped] = useImmer(false);

  const measure = () => {
    const element = textRef.current;
    if (element === null) {
      return;
    }
    setIsClipped(
      () =>
        element.scrollWidth > element.clientWidth ||
        element.scrollHeight > element.clientHeight
    );
  };

  // Measure up front as well as on hover: a tooltip whose title only arrives
  // after the pointer is already inside has to win a race with MUI's own
  // open decision, and the layout that decides this is settled at paint.
  useLayoutEffect(measure, [text, lines]);

  return (
    <IgTooltip title={isClipped ? text : ""} wrapperStyle={wrapperStyle}>
      <IgTypography
        ref={textRef}
        variant={variant}
        fontWeight={fontWeight}
        onMouseEnter={measure}
        sx={{
          color,
          fontSize,
          // Figma's card body metrics: 12px/1.5 (Body 2/Regular).
          lineHeight: lineHeight ?? 1.5,
          minWidth: 0,
          overflow: "hidden",
          ...(lines === 1
            ? {
                display: "block",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }
            : {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }),
        }}
      >
        {text}
      </IgTypography>
    </IgTooltip>
  );
};

export const RuleGraphNodeCard = ({
  data,
}: NodeProps<RuleGraphCardFlowNode>) => {
  const theme = useTheme();
  const {
    node,
    priority,
    headerCondition,
    headerSubject,
    isLaneHeader: isHeader,
    isSelected,
    onSelect,
    hasScenarioOverlay,
    skeleton,
  } = data;
  // While the graph is being rebuilt no evaluation is meaningful: skeleton
  // mode forces every tone neutral by dropping the overlay entirely.
  const overlay = skeleton ? undefined : data.overlay;
  const accent = KIND_ACCENTS[node.kind];
  const chips = metaChips(node);
  // The shopper-sees panel hangs on the node the evaluation ended on: the
  // output column carries the products face; the fallback terminal carries
  // the empty face in its own card slot.
  const result =
    node.kind === "output" || (!isHeader && node.kind === "fallback-terminal")
      ? overlay?.result
      : undefined;
  const terminalResult =
    node.kind === "fallback-terminal" ? overlay?.result : undefined;
  const isFaded = overlay?.dimmed === true;
  const isActive = overlay?.active === true;
  // Lane headers render as the Figma rule-row header line, not as step cards;
  // the terminal also carries its "Hide the upsell" card. They and the
  // shopper-sees panel bring their own surface, so the card drops its own
  // background, border, shadow and padding for them.
  const isChromeless = isHeader || result !== undefined;
  const { width: boxWidth, height: boxHeight } = ruleGraphFlowNodeSize({
    kind: node.kind,
    isLaneHeader: isHeader,
    hasResult: result !== undefined,
  });
  const rootRef = useNodeMeasurement<HTMLButtonElement>(
    node.id,
    data.onMeasure
  );
  const state = cardState({ skeleton, isFaded });
  const laneDotState = laneDotStateOf({
    skeleton,
    hasScenarioOverlay,
    isFaded,
  });
  const bodyText = overlay?.bodyText ?? nodeBodyText(node) ?? node.label;
  const textColor = isFaded
    ? theme.igPrimitives.gray.A100
    : theme.palette.text.primary;
  const kickerColor = isFaded
    ? theme.igPrimitives.gray.A100
    : theme.palette.text.placeholder;

  return (
    <Box
      ref={rootRef}
      aria-label={[accent.kindLabel, node.label].join(" ")}
      className="nodrag nopan"
      data-active={isActive ? "true" : undefined}
      data-kind={node.kind}
      data-state={state}
      // The skeleton is scenery, not a control: a plain div, so nothing
      // half-built sits in the tab order or promises a details panel.
      {...(skeleton
        ? { component: "div" as const }
        : {
            "aria-pressed": isSelected,
            component: "button" as const,
            onClick: () => onSelect(node.id),
            type: "button" as const,
          })}
      sx={(cardTheme) => ({
        alignItems: "flex-start",
        appearance: "none",
        backgroundColor: isChromeless
          ? "transparent"
          : cardTheme.igBackgroundColors.baseLightHigh,
        border: isChromeless
          ? 0
          : `1px solid ${cardBorderColor(cardTheme, { isSelected, isFaded })}`,
        borderRadius: 2,
        boxShadow: isChromeless || isFaded ? "none" : cardTheme.igShadows.low,
        boxSizing: "border-box",
        color: textColor,
        cursor: skeleton ? "default" : "pointer",
        display: "flex",
        flexDirection: "column",
        font: "inherit",
        // Figma: 6px between the card's kicker and body.
        gap: isChromeless ? 0 : 0.75,
        height: result || terminalResult ? "auto" : boxHeight,
        minHeight: boxHeight,
        // Figma: card content is pinned to the top — except the shopper-sees
        // panel, which centers on the card row it sits beside and is allowed
        // to overflow that row's height in both directions.
        justifyContent: result ? "center" : "flex-start",
        minWidth: 0,
        // The shopper-sees panel is content-sized and may run past the
        // declared node height; clipping it would slice its explanation.
        overflow: result || terminalResult ? "visible" : "hidden",
        padding: isChromeless ? 0 : "10px 12px",
        textAlign: "left",
        width: boxWidth,
        // The active run-walk beat settles into place; the guard exists
        // because the clock settles instantly under reduced motion, but a
        // beat mid-flight when the preference flips should still hold still.
        animation: isActive
          ? `${runLand} ${RUN_WALK_LAND_MS}ms ease-out`
          : "none",
        [REDUCED_MOTION]: { animation: "none" },
        "&:hover":
          skeleton || isChromeless
            ? undefined
            : {
                borderColor: cardTheme.igPrimitives.gray.A100,
                boxShadow: cardTheme.igShadows.low,
              },
        "&:focus-visible": {
          outline: `2px solid ${cardTheme.igPrimitives.base.A200}`,
          outlineOffset: 2,
        },
      })}
    >
      <Handle
        id="flow-target"
        type="target"
        position={Position.Left}
        isConnectable={false}
        style={{ opacity: 0 }}
      />
      <Handle
        id="fallback-target"
        type="target"
        position={Position.Top}
        isConnectable={false}
        // On header nodes the fallback chain is the left spine: it enters and
        // leaves at the lane dot, not the node's horizontal center.
        style={{ opacity: 0, ...(isHeader ? { left: 5 } : {}) }}
      />
      {result ? (
        <RuleGraphShopperOutput
          empty={result.empty}
          explanation={result.explanation}
          onEmpty={result.onEmpty}
          products={result.products}
          scenarioContext={result.scenarioContext}
        />
      ) : isHeader ? (
        <Stack gap={0} minWidth={0} width="100%">
          <RuleGraphLaneLabel
            color={textColor}
            // A branch under rebuild shows the shimmer bar in the chip's
            // place, so the phrase still has something after it and must not
            // absorb the missing chip — the copy would otherwise flip to
            // "anything" and back as the graph rebuilds.
            hasCondition={
              node.kind === "branch" &&
              (skeleton || headerCondition !== undefined)
            }
            state={laneDotState}
            subject={headerSubject}
            type={laneLabelTypeOf({
              kind: node.kind,
              priority,
              headerCondition,
            })}
          >
            {skeleton && node.kind === "branch" ? (
              // The header keeps its chipped structural phrase (the subject's
              // "When … " lead-in, never the always-matching variant) — the
              // phrase is derived from the kind and subject and survives a
              // rebuild; only the rule's own words shimmer.
              <SkeletonBar width={140} />
            ) : node.kind === "branch" && headerCondition !== undefined ? (
              // The Figma chips layout: the condition value(s) render as
              // chips after the bold header text.
              <IgChip
                color="info"
                label={headerCondition}
                size="small"
                sx={{ height: 24, maxWidth: 220, opacity: isFaded ? 0.4 : 1 }}
              />
            ) : null}
            {overlay?.chip ? (
              <IgChip
                color={overlay.chip.color}
                label={overlay.chip.label}
                size="small"
                sx={cardChipSx(isFaded)}
              />
            ) : null}
          </RuleGraphLaneLabel>
          {node.kind === "fallback-terminal" && terminalResult ? (
            // The scenario fell through to this row: its card slot renders
            // the shopper-sees empty face instead of the plain card.
            <Box
              sx={{
                ml: `${RULE_GRAPH_CARD_INDENT}px`,
                mt: `${RULE_GRAPH_HEADER_GAP}px`,
              }}
            >
              <RuleGraphShopperOutput
                empty={terminalResult.empty}
                explanation={terminalResult.explanation}
                onEmpty={terminalResult.onEmpty}
                products={terminalResult.products}
                scenarioContext={terminalResult.scenarioContext}
              />
            </Box>
          ) : node.kind === "fallback-terminal" ? (
            <Box
              sx={(cardTheme) => ({
                backgroundColor: isFaded
                  ? "transparent"
                  : cardTheme.igBackgroundColors.baseLightHigh,
                border: `1px solid ${
                  isFaded
                    ? cardTheme.igBorders.core.disabled
                    : cardTheme.igBorders.core.default
                }`,
                borderRadius: 2,
                boxShadow: isFaded ? "none" : cardTheme.igShadows.low,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                height: RULE_GRAPH_NODE_HEIGHT,
                justifyContent: "flex-start",
                ml: `${RULE_GRAPH_CARD_INDENT}px`,
                mt: `${RULE_GRAPH_HEADER_GAP}px`,
                overflow: "hidden",
                padding: "10px 12px",
                width: RULE_GRAPH_NODE_WIDTH,
              })}
            >
              {skeleton ? (
                <SkeletonBar width={150} />
              ) : (
                <ClippedText
                  color={textColor}
                  fontWeight={400}
                  lines={2}
                  text={fallbackTerminalBodyText(node)}
                  variant="body2"
                  wrapperStyle={{
                    display: "block",
                    minWidth: 0,
                    overflow: "hidden",
                    width: "100%",
                  }}
                />
              )}
            </Box>
          ) : null}
        </Stack>
      ) : skeleton ? (
        // The building state: the eyebrow slot and two body lines as shimmer
        // bars, inside the same fixed-size card, so the real text lands with
        // zero layout shift.
        <>
          <SkeletonBar width={44} />
          <SkeletonBar width={124} />
          <SkeletonBar width={86} />
        </>
      ) : (
        <>
          <IgTypography
            sx={{
              color: kickerColor,
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1.5,
              whiteSpace: "nowrap",
            }}
          >
            {KIND_EYEBROWS[node.kind]}
          </IgTypography>
          <ClippedText
            color={textColor}
            fontWeight={400}
            lines={2}
            text={bodyText}
            variant="body2"
            wrapperStyle={{
              display: "block",
              minWidth: 0,
              overflow: "hidden",
              width: "100%",
            }}
          />
          {overlay?.chip || chips.length > 0 ? (
            <Stack alignItems="center" direction="row" gap={0.5} minWidth={0}>
              {overlay?.chip ? (
                <IgChip
                  color={overlay.chip.color}
                  label={overlay.chip.label}
                  size="small"
                  sx={cardChipSx(isFaded)}
                />
              ) : null}
              {chips.map((chip) => (
                <IgChip
                  key={chip}
                  color="info"
                  label={chip}
                  size="small"
                  sx={cardChipSx(isFaded)}
                />
              ))}
            </Stack>
          ) : null}
        </>
      )}
      <Handle
        id="flow-source"
        type="source"
        position={Position.Right}
        isConnectable={false}
        style={{ opacity: 0 }}
      />
      <Handle
        id="fallback-source"
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={{ opacity: 0, ...(isHeader ? { left: 5 } : {}) }}
      />
    </Box>
  );
};

/**
 * The run's cart at the head of the rows. Scenery rather than a control: it
 * is the merchant's own pick said back, so there is no details panel behind
 * it and it stays out of the tab order. Its only handle is the flow source
 * the synthetic edge into the first row header leaves from.
 */
